import { useState, useMemo, useEffect, useCallback } from 'react';
import { Button, Input, Alert } from '@bbpos/ui';
import { formatCurrency } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { GLASS_FORM } from '@/constants/design-tokens';
import {
  lineFulfilment, lineMax, presetAllRemaining, presetDeliveredNotInvoiced, lineQtyError, buildLinePayload, formatQty,
} from '@/features/sales-orders';
import type { SalesOrder, StockShortfall, LineQuantityPayload, LineQuantityMode } from '@/features/sales-orders';
import { toLocalIsoDate } from '../purchase-orders/date-utils';
import { ArrowLeftIcon, CheckIcon, CalendarIcon, PackageIcon } from '../purchase-orders/icons';

export interface LineQuantityMeta {
  date: string;
  secondDate: string;
  reference: string;
  notes: string;
}

interface LineQuantityFormProps {
  mode: LineQuantityMode;
  order: SalesOrder;
  submitting: boolean;
  /** Deliver mode: the 409 shortfall list from the last attempt. */
  shortfalls?: StockShortfall[] | null;
  onSubmit: (items: LineQuantityPayload[], meta: LineQuantityMeta) => void;
  onBack: () => void;
}

// ─── Progress bar ───────────────────────────────────────────────────────────

function LineProgress({ ordered, existing, entering }: { ordered: number; existing: number; entering: number }) {
  const scale = Math.max(ordered, existing + entering, 1);
  const existingPct = (existing / scale) * 100;
  const enteringPct = (entering / scale) * 100;
  return (
    <div className="flex h-2.5 rounded-full bg-neutral-200/60 dark:bg-neutral-700/60 overflow-hidden">
      {existingPct > 0 && <div className="bg-success-500 transition-all duration-300" style={{ width: `${existingPct}%` }} />}
      {enteringPct > 0 && <div className="bg-indigo-400 dark:bg-indigo-500 transition-all duration-300" style={{ width: `${enteringPct}%` }} />}
    </div>
  );
}

// ─── Form ───────────────────────────────────────────────────────────────────

/**
 * One per-line quantity form for both "Record delivery" and "Raise invoice".
 * Quantities are whole units; a line left blank is skipped. The parent owns
 * the mutation and the redirect.
 */
export function LineQuantityForm({ mode, order, submitting, shortfalls, onSubmit, onBack }: LineQuantityFormProps) {
  const currency = useTenantCurrency();
  const isDeliver = mode === 'deliver';
  const [rows, setRows] = useState<Record<string, string>>(() => presetAllRemaining(order.items, mode));
  const [date, setDate] = useState(toLocalIsoDate(new Date()));
  const [secondDate, setSecondDate] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');

  const errors = useMemo(() => {
    const errs: Record<string, string> = {};
    for (const item of order.items) {
      const err = lineQtyError(rows[item.id] ?? '', lineMax(item, mode));
      if (err) errs[item.id] = err;
    }
    return errs;
  }, [rows, order.items, mode]);

  const payload = useMemo(() => buildLinePayload(rows), [rows]);
  const hasErrors = Object.keys(errors).length > 0;
  const canSubmit = payload.length > 0 && !hasErrors && !submitting;
  const units = payload.reduce((s, p) => s + p.quantity, 0);
  const shortNames = useMemo(() => new Set((shortfalls ?? []).map((s) => s.product_name)), [shortfalls]);

  const submit = useCallback(() => {
    if (!canSubmit) return;
    onSubmit(payload, { date, secondDate, reference: reference.trim(), notes: notes.trim() });
  }, [canSubmit, onSubmit, payload, date, secondDate, reference, notes]);

  // Keyboard: Ctrl+Enter to confirm, Esc to go back.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        submit();
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        onBack();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [submit, onBack]);

  return (
    <div className={`${GLASS_FORM} p-6 animate-slide-up`}>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
            {isDeliver ? 'Items to deliver' : 'Items to invoice'}
          </h3>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 mono-num">
            {payload.length} of {order.items.length} lines · {formatQty(units)} units
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            <Button variant="secondary" size="sm" onClick={() => setRows(presetAllRemaining(order.items, mode))}>All remaining</Button>
            {!isDeliver && (
              <Button variant="secondary" size="sm" onClick={() => setRows(presetDeliveredNotInvoiced(order.items))}>Delivered, not invoiced</Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => setRows({})}>Clear</Button>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-44">
            <label className="block text-[11px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
              {isDeliver ? 'Delivered date' : 'Invoice date'}
            </label>
            <Input type="date" value={date} onChange={setDate} />
          </div>
          {isDeliver ? (
            <div className="w-56">
              <label className="block text-[11px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
                Transporter / LR / vehicle
              </label>
              <Input value={reference} onChange={setReference} placeholder="Optional" />
            </div>
          ) : (
            <div className="w-44">
              <label className="block text-[11px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
                Due date
              </label>
              <Input type="date" value={secondDate} onChange={setSecondDate} />
            </div>
          )}
        </div>
      </div>

      {shortfalls && shortfalls.length > 0 && (
        <div className="mb-5">
          <Alert
            variant="danger"
            title="Insufficient stock"
            description={
              <ul className="mt-1 space-y-0.5 text-sm">
                {shortfalls.map((s) => (
                  <li key={s.product_name} className="tabular-nums">
                    <span className="font-medium">{s.product_name}</span>
                    {s.unavailable
                      ? ' — product unavailable'
                      : ` — requested ${formatQty(s.requested)}, available ${formatQty(s.available)}`}
                  </li>
                ))}
              </ul>
            }
          />
        </div>
      )}

      <div className="space-y-4">
        {order.items.map((item) => {
          const f = lineFulfilment(item);
          const max = lineMax(item, mode);
          const entered = Number(rows[item.id] ?? '') || 0;
          const existing = isDeliver ? f.delivered : f.invoiced;
          const disabled = max === 0;
          const short = shortNames.has(item.product_name);
          return (
            <div
              key={item.id}
              className={`relative pl-5 before:content-[''] before:absolute before:left-0 before:top-3 before:bottom-3 before:w-1 before:rounded-full before:bg-gradient-to-b before:from-brand-400 before:to-indigo-600 rounded-xl backdrop-blur-sm p-4 shadow-[0_4px_16px_-10px_rgba(15,23,42,0.18)] hover:shadow-[0_8px_24px_-12px_rgba(37,99,235,0.35)] transition-shadow ${
                short ? 'bg-danger-50/60 dark:bg-danger-900/20' : 'bg-white/60 dark:bg-neutral-900/40'
              } ${disabled ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <PackageIcon className="h-4 w-4 text-brand-600 dark:text-brand-400" />
                    <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-100 truncate">{item.product_name}</p>
                    <span className="text-xs text-neutral-500 dark:text-neutral-400 font-mono">{item.product_sku}</span>
                  </div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 mono-num">
                    Ordered {formatQty(f.ordered)} · Delivered {formatQty(f.delivered)} · Invoiced {formatQty(f.invoiced)} · {formatCurrency(item.unit_price, currency)} each
                  </p>
                </div>
                <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mono-num">
                  {disabled ? (isDeliver ? 'Fully delivered' : 'Fully invoiced') : `Remaining: ${formatQty(max)}`}
                </span>
              </div>

              <LineProgress ordered={f.ordered} existing={existing} entering={entered} />

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                <div>
                  <label className="block text-[11px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
                    {isDeliver ? 'Qty to deliver' : 'Qty to invoice'}
                  </label>
                  <Input
                    type="number"
                    value={rows[item.id] ?? ''}
                    onChange={(v) => setRows((prev) => ({ ...prev, [item.id]: v }))}
                    placeholder={disabled ? '—' : `Up to ${formatQty(max)}`}
                    disabled={disabled}
                    aria-label={`${isDeliver ? 'Quantity to deliver' : 'Quantity to invoice'} for ${item.product_name}`}
                  />
                  {errors[item.id] && <p className="text-[11px] text-danger-600 mt-0.5">{errors[item.id]}</p>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6">
        <label className="block text-[11px] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">Notes</label>
        <Input value={notes} onChange={setNotes} placeholder={isDeliver ? 'e.g. Delivered to site gate, signed by security' : 'Printed on the invoice'} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mt-8 pt-6 border-t border-neutral-200/70 dark:border-neutral-700/50">
        <Button variant="secondary" onClick={onBack}>
          <ArrowLeftIcon className="h-4 w-4 mr-1.5" />
          Back to order
        </Button>
        <div className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400">
          <CalendarIcon className="h-3.5 w-3.5" />
          <span className="mono-num">Ctrl+Enter to confirm · Esc to cancel</span>
        </div>
        <Button variant="primary" onClick={submit} disabled={!canSubmit} loading={submitting}>
          <CheckIcon className="h-4 w-4 mr-1.5" />
          {isDeliver ? 'Confirm delivery' : 'Raise invoice'}
        </Button>
      </div>
    </div>
  );
}
